import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AssessmentContext } from '../components/assessment/AssessmentContext';
import QuestionsStep from '../components/assessment/QuestionsStep';
import LeadCaptureStep from '../components/assessment/LeadCaptureStep';
import ResultsStep from '../components/assessment/ResultsStep';

const API = process.env.REACT_APP_BACKEND_URL;

export default function Assessment() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [questionsData, setQuestionsData] = useState(null);
  const [currentStep, setCurrentStep] = useState('questions');
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [leadInfo, setLeadInfo] = useState({
    first_name: '',
    last_name: '',
    email: '',
    company_name: '',
    job_title: '',
    team_size: '',
    industry: '',
    marketing_consent: false,
  });
  const [results, setResults] = useState(null);

  // Fetch questions on mount
  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        const { data } = await axios.get(`${API}/api/assessment/questions`);
        setQuestionsData(data);
      } catch (err) {
        toast.error('Failed to load assessment');
      } finally {
        setLoading(false);
      }
    };
    fetchQuestions();
  }, []);

  const handleAnswerSelect = (questionId, score) => {
    setAnswers((prev) => ({ ...prev, [questionId]: score }));
  };

  const handleNext = () => {
    if (currentQuestionIndex < questionsData.questions.length - 1) {
      setCurrentQuestionIndex((prev) => prev + 1);
    } else {
      setCurrentStep('leadCapture');
    }
  };

  const handlePrev = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex((prev) => prev - 1);
    }
  };

  const handleLeadInfoChange = (e) => {
    setLeadInfo((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        lead_info: leadInfo,
        answers: Object.entries(answers).map(([question_id, score]) => ({
          question_id,
          score,
        })),
      };
      const { data } = await axios.post(`${API}/api/assessment/submit`, payload);
      setResults(data);
      setCurrentStep('results');
    } catch (err) {
      toast.error('Failed to submit assessment. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const currentQuestion = questionsData?.questions?.[currentQuestionIndex];
  const progress = questionsData
    ? ((currentQuestionIndex + 1) / questionsData.questions.length) * 100
    : 0;
  const isCurrentAnswered = currentQuestion && answers[currentQuestion.id];

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-cyan-400/30 border-t-cyan-400 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Loading assessment...</p>
        </div>
      </div>
    );
  }

  const ctx = {
    navigate, questionsData, currentStep, setCurrentStep,
    currentQuestionIndex, setCurrentQuestionIndex, answers, setAnswers,
    leadInfo, setLeadInfo, results, setResults,
    submitting, setSubmitting,
    handleAnswerSelect, handleNext, handlePrev, handleLeadInfoChange, handleSubmit,
    currentQuestion, progress, isCurrentAnswered,
  };

  return (
    <AssessmentContext.Provider value={ctx}>
      {currentStep === 'questions' && currentQuestion && <QuestionsStep />}
      {currentStep === 'leadCapture' && <LeadCaptureStep />}
      {currentStep === 'results' && results && <ResultsStep />}
    </AssessmentContext.Provider>
  );
}
